<template>
  <section class="zone-tournament-item" :data-qe-id="qeIdRoot">
    <div v-if="isInternalZone(zoneSelectionUi.tournamentType)" class="internal">Internal</div>
    <div v-if="tournamentInfo" class="layout-wrap">
      <div :class="`zone-tournament-image ${zoneSelectionUi.zoneId}`">
        <div class="zone-tournament-image-cont">
          <img :src="zoneLogo" class="image-inner" alt="...no image" />
        </div>
      </div>
      <div class="zone-tournament-info-wrapper">
        <div class="zone-tournament-info">
          <c-lobby-status
            v-if="zoneSelectionUi.tournamentStatus !== TournamentStatus.FINISHED"
            :timers="zoneSelectionUi.lobbyTimers"
            @lobby-timer-complete="onLobbyTimerComplete"
          />
          <div class="tournament-title">
            <img v-if="!zoneSelectionUi.isMasters && tournamentInfo.image" :src="loadImage" />
            <h3>{{ tournamentInfo.name }}</h3>
          </div>
          <div class="tournament-info-layout">
            <c-tournament-info
              :tournament-ui="tournamentInfoUi"
              class="tournament-info-box"
              competitionDate
              registrationDate
              totalWin
            />
          </div>
        </div>
        <div class="tournament-button-container">
          <t-button @click="enterZone" yellow m data-qe-id="button-enter">
            {{ buttonCaption }}
          </t-button>
        </div>
      </div>
      <c-tournament-status-indicator :status="zoneSelectionUi.tournamentStatus" data-qe-id="tournament-status" />
    </div>

    <div v-else class="layout-wrap">
      <div :class="`zone-tournament-image ${zoneSelectionUi.zoneId}`">
        <div class="zone-tournament-image-cont">
          <img :src="zoneLogo" class="image-inner" alt="...no current tournament" />
        </div>
      </div>
      <div class="zone-tournament-info-wrapper">
        <div class="zone-tournament-info">
          <h3 class="no-tournament">{{ $t('zoneSelection.zone.noTournamentsPlanned') }}</h3>
        </div>
        <div class="tournament-button-container">
          <t-button @click="enterZone" yellow m data-qe-id="button-enter">
            {{ buttonCaption }}
          </t-button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useStore } from '@/store'
import { TOURNAMENT_TYPE, TournamentTypeToName } from '@/model/TournamentType'
import { ZoneSelectionUi } from '@/model/ZoneSelection'
import { TournamentStatus } from '@/model/TournamentStatus'
import CTournamentInfo from '@/components/CTournamentInfo.vue'
import CLobbyStatus from '@/components/CLobbyStatus.vue'
import CTournamentStatusIndicator from '@/components/CTournamentStatusIndicator.vue'
import { isInternal } from '@/store/lib/util'
import { jslog } from '@gd/jslog'
import { TournamentUi } from '@/model/Tournament'

const props = withDefaults(defineProps<{ language: string; zoneSelectionUi: ZoneSelectionUi }>(), {
  language: 'en',
})
const emit = defineEmits(['onGoToZone'])
const router = useRouter()
const store = useStore()
const { t } = useI18n()

const logosAvailableForLocales = ['de', 'nl', 'fr']
/**
 * TODO replace this by the real name from i18n
 */
function tournamentTypeName(TOURNAMENT_TYPE: TOURNAMENT_TYPE) {
  return TournamentTypeToName[TOURNAMENT_TYPE]
}

const loadImage = computed(() => {
  let logo
  try {
    logo = require(`@/assets/logos/${tournamentInfo.value.image}.png`)
  } catch (err: unknown) {
    jslog.send({ error: new Error(`Unable to load logo ${tournamentInfo.value.image}.png`) })
  }
  return logo
})

const zoneLogo = computed(() => {
  if (
    props.zoneSelectionUi.zoneId === TOURNAMENT_TYPE.SKA_TOURNAMENT &&
    isInternalZone(props.zoneSelectionUi.tournamentType)
  ) {
    return require(`@/assets/zone-overview/internal/skat_matze.png`)
  }

  return require(`@/assets/zone-overview/${props.zoneSelectionUi.zoneId}${logoLocale.value}.png`)
})

const qeIdRoot = computed(() => {
  return `zone-tournament-item-${tournamentTypeName(props.zoneSelectionUi.tournamentType)}`
})

const buttonCaption = computed(() => {
  return t(`zoneSelection.zone.button_${props.zoneSelectionUi.zoneId}`)
})
// TODO vue3 - the object is completely different from what is expected in "c-tournament-info"
const tournamentInfoUi = computed((): TournamentUi => {
  const tournamentInfoUi = props.zoneSelectionUi.tournamentInfo
  return {
    ...tournamentInfoUi,
    metaData: {
      totalWin: tournamentInfoUi.totalWin,
      numberOfTickets: 0,
      entryFee: '0',
      restartFee: '0',
      playersPerGame: 0,
      numberOfGames: 0,
      roundsPerGame: 0,
      rejoinsUnlimited: false,
      rejoinsRemaining: 0,
      clearancePhase: false,
      distributionSchema: '',
      gameTechs: [],
      image: '',
    },
    tournamentStatus: props.zoneSelectionUi.tournamentStatus,
  } as any
})

const tournamentInfo = computed(() => {
  return props.zoneSelectionUi.tournamentInfo
})

const logoLocale = computed(() => {
  return props.zoneSelectionUi.zoneId === TOURNAMENT_TYPE.ROM_TOURNAMENT &&
    logosAvailableForLocales.indexOf(props.language) !== -1
    ? `_${props.language}`
    : ''
})

function enterZone() {
  emit('onGoToZone', props.zoneSelectionUi.tournamentType)
}

function isInternalZone(id: string) {
  return isInternal(id)
}

function onLobbyTimerComplete() {
  store.dispatch('fetchZoneTeasers').catch((err: any) => {
    if (err === 401) {
      router.replace('/extern/login')
    }
  })
}
</script>

<style lang="less">
@import '~@npm/@gd/common-styles/lib/color-vars';
@import '../styles/vars';
@import '~@npm/@gd/common-styles/dist/card';

@break1: 1000px;
@break2: 800px;
@break3: 660px;

.zone-tournament-item {
  @media screen and (max-width: @break2) {
    max-width: 80%;
  }
  @media screen and (max-width: @break1) {
    max-width: 100%;
  }
  .internal {
    position: absolute;
    padding: 1em 1.25em 1em 1em;
    border-radius: 0 0 16px 0;
    background: @c1-f;
    color: white;
    box-shadow: 2px 2px 4px rgba(0, 0, 0, 0.4);
    text-shadow: 0 0 3px @c1-j;
  }
  h3 {
    font-size: 1.63em;
    line-height: 1.15em;
    margin: 0.613em 0;
  }
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  .card();
  .zone-tournament-image {
    display: flex;
    flex-grow: 0;
    padding: 3px;
    line-height: 0;
    background: @c1-b;
    overflow: hidden;
    border-radius: 3px 0 0 3px;
    flex-shrink: 0;
    width: 22em;

    min-height: 15em;

    @media screen and (max-width: @break2) {
      width: 15em;
      width: 100%;
      margin: 0;
      height: 40vw;
      max-height: 18em;
      overflow: hidden;
      border-radius: 3px 3px 0 0;
    }

    &-cont {
      display: flex;
      overflow: hidden;
      background-size: cover;
      background-position: center top;
      min-width: 100%;
      justify-content: center;
      align-items: center;
      padding: 1em;
      @media screen and (max-width: @break2) {
        width: 100%;
        margin: 0;
        overflow: hidden;
      }
    }
    &.romme {
      .zone-tournament-image-cont {
        background-image: url(../assets/zone-overview/romme_bg.jpg);
      }
    }
    &.skat {
      .zone-tournament-image-cont {
        background-image: url(../assets/zone-overview/skat_bg.jpg);
      }
    }
    &.skatmasters {
      .zone-tournament-image-cont {
        background-image: url(../assets/zone-overview/skatmasters_bg.jpg);
      }
    }
    &.rommemasters {
      .zone-tournament-image-cont {
        background-image: url(../assets/zone-overview/rommemasters_bg.jpg);
      }
    }
    &.skatmasters,
    &.rommemasters {
      background: linear-gradient(
        60deg,
        rgba(113, 83, 13, 1) 0%,
        rgba(240, 235, 157, 1) 45%,
        rgba(113, 83, 13, 1) 120%
      );
    }
    &.skat {
      background: @colorScheme--midGreen;
    }
    &.romme {
      background: @colorScheme--red;
    }
    &.not-available {
      background: @c0-a;
    }
    .image-inner {
      max-width: 100%;
      height: auto;
      justify-self: center;
      align-self: center;
    }
  }
  .zone-tournament-info-wrapper {
    display: flex;
    flex-grow: 1;
    align-content: space-between;
    @media screen and (max-width: @break1) {
      align-items: flex-start;
      flex-wrap: wrap;
    }
  }

  .zone-tournament-info {
    padding: @smallerspacing 0 @smallerspacing @smallerspacing;
    flex-grow: 1;
    .no-tournament {
      color: @c0-f;
    }

    .name {
      @media screen and (max-width: @break2) {
        text-align: right;
      }
    }
    .value {
      @media screen and (max-width: @break2) {
        text-align: left;
      }
    }
    @media screen and (max-width: @break1) {
      padding: @smallerspacing @biggerspacing @smallerspacing @smallerspacing;
    }
    @media screen and (max-width: @break2) {
      padding: @smallerspacing;
      text-align: center;
    }
  }
  .layout-wrap {
    display: flex;
    @media screen and (max-width: @break2) {
      display: block;
    }
  }
  .tournament-info-layout {
  }
  .tournament-info {
    margin-bottom: 0.75em;
    line-height: 1.4em;
    .layout {
      padding: 0;
    }
  }
  .tournament-button-container {
    align-self: flex-end;
    padding: @smallerspacing;
    flex-shrink: 0;
    @media screen and (max-width: @break1) {
      flex: 0 0 100%;
      align-self: flex-start;
      padding-top: 0;
    }
    @media screen and (max-width: @break2) {
      flex: 0 0 100%;
      text-align: center;
      padding: 0 0 @smallerspacing 0;
    }
  }
  .tournament-title {
    display: flex;
    justify-items: center;
    align-items: center;
    img {
      margin: 0.4em 0 0.4em -0.25em;
      + h3 {
        margin-left: 0.5em;
      }
    }
    @media screen and (max-width: @break2) {
      justify-content: center;
    }
  }
  .lobby-status-text {
    font-size: 0.875rem;
  }
}
</style>
